import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, FolderKanban, Building2, Lock } from 'lucide-react';
import { projectsApi, organizationsApi } from '@/lib/api';
import { Select } from '@solid/ui';
import { Button } from '@/components/ui/Button';
import { useAuthStore, getManagedPartnerAgency } from '@/stores/auth.store';
import { assignableClientOrgs } from '@/lib/client-orgs';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (project: any) => void;
  /** The organization this project will belong to */
  organizationId?: string;
  /** Display name of the organization */
  organizationName?: string;
  /** If true, this is an internal agency project (no client organization) */
  isInternal?: boolean;
}

export function CreateProjectModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  organizationId,
  organizationName,
  isInternal = false,
}: CreateProjectModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  // '' = internal to the agency; otherwise the client organization's id.
  const [clientOrgId, setClientOrgId] = useState('');

  // The agency the project belongs to: Solid Digital, or the partner agency
  // this user runs (a partner agency's admins create projects for their own).
  const agencyOrg =
    user?.organizations?.find((org: any) => org.type === 'AGENCY') ?? getManagedPartnerAgency(user);

  // Agency staff pick the organization here. It used to be read-only and
  // taken from the projects page's filter, so with "All projects" selected
  // every new project silently became an internal Solid Digital one.
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
    enabled: isOpen && !!agencyOrg,
  });
  const orgOptions = [
    { value: '', label: `${agencyOrg?.name ?? 'Agency'} (internal)` },
    ...assignableClientOrgs(organizations, agencyOrg)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((org) => ({ value: org.id, label: org.name })),
  ];

  // Only org owners can create private projects
  const isAgencyOwner = agencyOrg?.role === 'org_owner';

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setIsPrivate(false);
      // Start from the page's current filter, then let the writer change it.
      setClientOrgId(isInternal ? '' : (organizationId ?? ''));
    }
  }, [isOpen, isInternal, organizationId]);
  
  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; organizationId: string; clientOrganizationId?: string; isPrivate?: boolean }) =>
      projectsApi.create(data),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setName('');
      setDescription('');
      setIsPrivate(false);
      onClose();
      onSuccess?.(project);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    // All projects belong to the agency organization
    const projectOrgId = agencyOrg?.id || organizationId;

    if (!projectOrgId) return;

    // Agency staff chose in the form; anyone else gets the page's organization.
    const chosenClientOrgId = agencyOrg ? clientOrgId || undefined : isInternal ? undefined : organizationId;

    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      // Project always belongs to the agency
      organizationId: projectOrgId,
      // For client projects, set the client organization for auto-adding members
      // For internal projects, don't set a client organization
      clientOrganizationId: chosenClientOrgId,
      isPrivate: isPrivate || undefined,
    });
  };
  
  if (!isOpen) return null;
  
  // Determine display name
  const displayOrgName = organizationName || agencyOrg?.name || 'Organization';
  const selectedIsInternal = agencyOrg ? clientOrgId === '' : isInternal;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <FolderKanban className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-heading-4 text-secondary">Create New Project</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-5">
            {/* Organization: a real choice for agency staff, fixed for everyone else */}
            {agencyOrg ? (
              <div>
                <label htmlFor="project-org" className="block text-sm font-medium text-secondary mb-2">
                  Organization
                </label>
                <Select
                  id="project-org"
                  options={orgOptions}
                  value={clientOrgId}
                  onChange={(e) => setClientOrgId(e.target.value)}
                  disabled={createMutation.isPending}
                />
                <p className="mt-1.5 text-xs text-grey-500">
                  {selectedIsInternal
                    ? 'Internal to your agency. No client team is added.'
                    : 'Client team members will be added to the project.'}
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  {isInternal ? 'Internal Project For' : 'Client Organization'}
                </label>
                <div className="flex items-center gap-3 px-4 py-3 bg-grey-50 rounded-lg border border-grey-200">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isInternal ? 'bg-primary/10' : 'bg-accent/10'}`}>
                    <Building2 className={`w-4 h-4 ${isInternal ? 'text-primary' : 'text-accent'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-secondary">{displayOrgName}</p>
                    <p className="text-xs text-grey-500">
                      {isInternal
                        ? 'This project will be internal to your agency'
                        : 'Client team members will be auto-added'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-secondary mb-2">
                Project Name <span className="text-error">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Q1 Marketing Campaign"
                className="input"
                required
                autoFocus
              />
            </div>
            
            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-secondary mb-2">
                Description <span className="text-grey-400">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the project..."
                rows={3}
                className="input resize-none"
              />
            </div>

            {/* Private Project Toggle - only visible to org owners */}
            {isAgencyOwner && (
              <label className="flex items-center gap-3 p-4 rounded-xl bg-grey-50 border border-grey-200 cursor-pointer hover:bg-grey-100 transition-colors">
                <div className={`w-10 h-6 rounded-full relative transition-colors ${isPrivate ? 'bg-primary' : 'bg-grey-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${isPrivate ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </div>
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="sr-only"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-grey-600" />
                    <span className="text-sm font-medium text-secondary">Private Project</span>
                  </div>
                  <p className="text-xs text-grey-500 mt-0.5">Only visible to members you add. Even admins cannot see this project unless they are members.</p>
                </div>
              </label>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
